import * as THREE from 'three';
import { TAU, smoothstep, clamp, lerp } from './util.js';

/**
 * Pure x/z layout of the world: the van's closed route, the river + lakes
 * (with the bridge crossing) and an occupancy grid used to place things
 * without them running into each other. No heights in here.
 */

export const WORLD_R = 200; // terrain half-size
export const PLAZA_R = 9;
export const WATER_LEVEL = 0;
export const VILLAGE_H = 0.9; // height of the flattened village floor

// ------------------------------------------------------------------ route
function routeRadius(th) {
  return 42 + 7 * Math.sin(2 * th + 0.6) + 4 * Math.sin(3 * th + 2.0) + 2.5 * Math.sin(5 * th);
}

const ROUTE_CTRL_N = 30;
const ctrl = [];
for (let i = 0; i < ROUTE_CTRL_N; i++) {
  const th = (i / ROUTE_CTRL_N) * TAU;
  const r = routeRadius(th);
  ctrl.push(new THREE.Vector3(r * Math.cos(th), 0, r * Math.sin(th)));
}
export const routeCurve = new THREE.CatmullRomCurve3(ctrl, true, 'centripetal');
routeCurve.arcLengthDivisions = 4000;
export const ROUTE_LEN = routeCurve.getLength();

const RS = 0.5; // sample spacing (m)
export const ROUTE_N = Math.round(ROUTE_LEN / RS);
export const ROUTE_DS = ROUTE_LEN / ROUTE_N;
const rX = new Float32Array(ROUTE_N);
const rZ = new Float32Array(ROUTE_N);
const rTX = new Float32Array(ROUTE_N);
const rTZ = new Float32Array(ROUTE_N);
const rCurv = new Float32Array(ROUTE_N); // signed curvature (1/m), +ve = turning toward +heading angle
for (let i = 0; i < ROUTE_N; i++) {
  const u = i / ROUTE_N;
  const p = routeCurve.getPointAt(u);
  const t = routeCurve.getTangentAt(u);
  rX[i] = p.x;
  rZ[i] = p.z;
  rTX[i] = t.x;
  rTZ[i] = t.z;
}
{
  const head = new Float32Array(ROUTE_N);
  for (let i = 0; i < ROUTE_N; i++) head[i] = Math.atan2(rTX[i], rTZ[i]); // yaw: 0 = +Z
  const raw = new Float32Array(ROUTE_N);
  for (let i = 0; i < ROUTE_N; i++) {
    const a = head[(i + 2) % ROUTE_N], b = head[(i - 2 + ROUTE_N) % ROUTE_N];
    let d = a - b;
    while (d > Math.PI) d -= TAU;
    while (d < -Math.PI) d += TAU;
    raw[i] = d / (4 * ROUTE_DS);
  }
  // smooth
  for (let i = 0; i < ROUTE_N; i++) {
    let s = 0;
    for (let k = -8; k <= 8; k++) s += raw[(i + k + ROUTE_N) % ROUTE_N];
    rCurv[i] = s / 17;
  }
}

const _out = { x: 0, z: 0, tx: 0, tz: 1, yaw: 0, curv: 0 };
/** Position / tangent / curvature of the route at arc length s (metres, wraps). */
export function routeAt(s, out = _out) {
  const f = ((s % ROUTE_LEN) + ROUTE_LEN) % ROUTE_LEN / ROUTE_DS;
  const i0 = Math.floor(f) % ROUTE_N;
  const i1 = (i0 + 1) % ROUTE_N;
  const k = f - Math.floor(f);
  out.x = lerp(rX[i0], rX[i1], k);
  out.z = lerp(rZ[i0], rZ[i1], k);
  let tx = lerp(rTX[i0], rTX[i1], k), tz = lerp(rTZ[i0], rTZ[i1], k);
  const l = Math.hypot(tx, tz) || 1;
  out.tx = tx / l;
  out.tz = tz / l;
  out.yaw = Math.atan2(out.tx, out.tz);
  out.curv = lerp(rCurv[i0], rCurv[i1], k);
  return out;
}

// ------------------------------------------------------------------ bridge + river
// The river crosses the route perpendicularly at a control point.
const BRIDGE_CTRL = 4;
const bIdx = Math.round(((BRIDGE_CTRL / ROUTE_CTRL_N) * ROUTE_N)); // approx sample idx of that control
let bestI = 0, bestD = 1e9;
{
  const B = ctrl[BRIDGE_CTRL];
  for (let i = 0; i < ROUTE_N; i++) {
    const d = Math.hypot(rX[i] - B.x, rZ[i] - B.z);
    if (d < bestD) { bestD = d; bestI = i; }
  }
}
export const BRIDGE = { x: rX[bestI], z: rZ[bestI], s: bestI * ROUTE_DS, tx: rTX[bestI], tz: rTZ[bestI], dx: 0, dz: 0 };
{
  // river direction: perpendicular to route, pointing away from the village
  let dx = BRIDGE.tz, dz = -BRIDGE.tx;
  if (dx * BRIDGE.x + dz * BRIDGE.z < 0) { dx = -dx; dz = -dz; }
  BRIDGE.dx = dx;
  BRIDGE.dz = dz;
}

const RIVER_S0 = -19, RIVER_S1 = 30;
const riverPts = [];
for (let s = RIVER_S0; s <= RIVER_S1 + 0.01; s += 5.5) {
  const off = 5.5 * Math.sin(s * 0.13 + 0.7) * smoothstep(5, 15, Math.abs(s));
  riverPts.push(new THREE.Vector3(
    BRIDGE.x + BRIDGE.dx * s + BRIDGE.tx * off,
    0,
    BRIDGE.z + BRIDGE.dz * s + BRIDGE.tz * off,
  ));
}
export const riverCurve = new THREE.CatmullRomCurve3(riverPts, false, 'centripetal');
const RIVER_SAMPLES = 60;
const rvx = new Float32Array(RIVER_SAMPLES + 1);
const rvz = new Float32Array(RIVER_SAMPLES + 1);
const rvw = new Float32Array(RIVER_SAMPLES + 1);
for (let i = 0; i <= RIVER_SAMPLES; i++) {
  const u = i / RIVER_SAMPLES;
  const p = riverCurve.getPointAt(u);
  rvx[i] = p.x;
  rvz[i] = p.z;
  rvw[i] = 5.2 + 0.9 * Math.sin(u * 9 + 1.0) + 1.2 * smoothstep(0.75, 1, u);
}
export const riverPolyline = Array.from({ length: RIVER_SAMPLES + 1 }, (_, i) => ({ x: rvx[i], z: rvz[i], w: rvw[i] }));

export const lakes = [
  { x: BRIDGE.x + BRIDGE.dx * 35, z: BRIDGE.z + BRIDGE.dz * 35, r: 9.5, bed: -1.6, seed: 1.3 }, // outer lake, foot of the hills
  { x: BRIDGE.x + BRIDGE.dx * RIVER_S0, z: BRIDGE.z + BRIDGE.dz * RIVER_S0, r: 5.0, bed: -1.25, seed: 4.1 }, // village pond
];
export const POND = lakes[1];
export const LAKE = lakes[0];

const BANK = 3.0;
const RIVER_BED = -1.05;

function distToRiver(x, z) {
  let best = 1e9, bw = 5;
  for (let i = 0; i < RIVER_SAMPLES; i++) {
    const ax = rvx[i], az = rvz[i], bx = rvx[i + 1], bz = rvz[i + 1];
    const abx = bx - ax, abz = bz - az;
    const t = clamp(((x - ax) * abx + (z - az) * abz) / (abx * abx + abz * abz + 1e-9), 0, 1);
    const d = Math.hypot(x - (ax + abx * t), z - (az + abz * t));
    if (d < best) { best = d; bw = lerp(rvw[i], rvw[i + 1], t); }
  }
  return { d: best, w: bw };
}

/** 0..1 how much the terrain is pushed down into a river bed / lake here. */
export function carveAt(x, z) {
  let c = 0;
  for (const L of lakes) {
    const dx = x - L.x, dz = z - L.z;
    const d2 = dx * dx + dz * dz;
    const reach = L.r + BANK + 4;
    if (d2 > reach * reach) continue;
    const d = Math.sqrt(d2);
    const ang = Math.atan2(dz, dx);
    const rr = L.r * (1 + 0.16 * Math.sin(ang * 3 + L.seed) + 0.07 * Math.sin(ang * 5 + L.seed * 2));
    c = Math.max(c, 1 - smoothstep(rr * 0.55, rr + BANK, d));
  }
  const rv = distToRiver(x, z);
  if (rv.d < rv.w + BANK + 2) {
    c = Math.max(c, 1 - smoothstep(rv.w * 0.32, rv.w * 0.5 + BANK, rv.d));
  }
  return c;
}
export function bedDepthAt(x, z) {
  // deeper in lakes, shallower in river
  let lake = 0;
  for (const L of lakes) {
    const d = Math.hypot(x - L.x, z - L.z);
    lake = Math.max(lake, 1 - smoothstep(L.r * 0.4, L.r * 1.25, d));
  }
  return lerp(RIVER_BED, lakes[0].bed, lake);
}

// arc-length range of the route that is over water (=> bridge)
export const BRIDGE_SPAN = { s0: BRIDGE.s - 9, s1: BRIDGE.s + 9 };
{
  let lo = 0, hi = 0, found = false;
  for (let d = 0; d < 30; d += 0.5) {
    const a = routeAt(BRIDGE.s - d), b = routeAt(BRIDGE.s + d);
    if (!found && carveAt(a.x, a.z) < 0.08 && carveAt(b.x, b.z) < 0.08) { lo = d; found = true; }
  }
  if (found) {
    BRIDGE_SPAN.s0 = BRIDGE.s - lo - 1.5;
    BRIDGE_SPAN.s1 = BRIDGE.s + lo + 1.5;
  }
}

/** Block every grid cell touched by the river / lakes (incl. sloped banks). */
export function markWater(occ, margin = 1.4) {
  const scan = (cx, cz, reach) => {
    for (let z = cz - reach; z <= cz + reach; z += 1) {
      for (let x = cx - reach; x <= cx + reach; x += 1) {
        if (carveAt(x, z) > 0.001) occ.disc(x, z, margin, 1);
      }
    }
  };
  for (let i = 0; i <= RIVER_SAMPLES; i += 2) scan(rvx[i], rvz[i], rvw[i] + BANK + 3);
  for (const L of lakes) scan(L.x, L.z, L.r * 1.3 + BANK + 5);
}

// ------------------------------------------------------------------ occupancy grid
export class Occupancy {
  constructor(size = WORLD_R * 2, cell = 1) {
    this.cell = cell;
    this.half = size / 2;
    this.n = Math.ceil(size / cell);
    this.data = new Uint8Array(this.n * this.n);
  }
  _ix(x) { return Math.floor((x + this.half) / this.cell); }
  inBounds(ix, iz) { return ix >= 0 && iz >= 0 && ix < this.n && iz < this.n; }
  disc(x, z, r, val = 1) {
    const x0 = this._ix(x - r), x1 = this._ix(x + r), z0 = this._ix(z - r), z1 = this._ix(z + r);
    const r2 = r * r;
    for (let iz = z0; iz <= z1; iz++) {
      for (let ix = x0; ix <= x1; ix++) {
        if (!this.inBounds(ix, iz)) continue;
        const cx = (ix + 0.5) * this.cell - this.half, cz = (iz + 0.5) * this.cell - this.half;
        if ((cx - x) ** 2 + (cz - z) ** 2 <= r2) this.data[iz * this.n + ix] = val;
      }
    }
  }
  /** Oriented rectangle (angle = rotation about Y). */
  rect(cx, cz, w, d, angle, margin = 0, val = 1) {
    const ca = Math.cos(angle), sa = Math.sin(angle);
    const hw = w / 2 + margin, hd = d / 2 + margin;
    const ext = Math.hypot(hw, hd);
    const x0 = this._ix(cx - ext), x1 = this._ix(cx + ext), z0 = this._ix(cz - ext), z1 = this._ix(cz + ext);
    for (let iz = z0; iz <= z1; iz++) {
      for (let ix = x0; ix <= x1; ix++) {
        if (!this.inBounds(ix, iz)) continue;
        const px = (ix + 0.5) * this.cell - this.half - cx, pz = (iz + 0.5) * this.cell - this.half - cz;
        const lx = px * ca - pz * sa, lz = px * sa + pz * ca;
        if (Math.abs(lx) <= hw && Math.abs(lz) <= hd) this.data[iz * this.n + ix] = val;
      }
    }
  }
  polyline(pts, r, val = 1, step = 1) {
    for (let i = 0; i < pts.length - 1; i++) {
      const a = pts[i], b = pts[i + 1];
      const len = Math.hypot(b.x - a.x, b.z - a.z);
      const n = Math.max(1, Math.ceil(len / step));
      for (let k = 0; k <= n; k++) this.disc(lerp(a.x, b.x, k / n), lerp(a.z, b.z, k / n), r, val);
    }
  }
  at(x, z) {
    const ix = this._ix(x), iz = this._ix(z);
    return this.inBounds(ix, iz) ? this.data[iz * this.n + ix] : 1;
  }
  isFree(x, z, r = 0) {
    if (r <= 0.5) return this.at(x, z) === 0;
    const x0 = this._ix(x - r), x1 = this._ix(x + r), z0 = this._ix(z - r), z1 = this._ix(z + r);
    const r2 = r * r;
    for (let iz = z0; iz <= z1; iz++) {
      for (let ix = x0; ix <= x1; ix++) {
        if (!this.inBounds(ix, iz)) return false;
        const cx = (ix + 0.5) * this.cell - this.half, cz = (iz + 0.5) * this.cell - this.half;
        if ((cx - x) ** 2 + (cz - z) ** 2 <= r2 && this.data[iz * this.n + ix]) return false;
      }
    }
    return true;
  }
  rectFree(cx, cz, w, d, angle, margin = 0) {
    const ca = Math.cos(angle), sa = Math.sin(angle);
    const hw = w / 2 + margin, hd = d / 2 + margin;
    const ext = Math.hypot(hw, hd);
    const x0 = this._ix(cx - ext), x1 = this._ix(cx + ext), z0 = this._ix(cz - ext), z1 = this._ix(cz + ext);
    for (let iz = z0; iz <= z1; iz++) {
      for (let ix = x0; ix <= x1; ix++) {
        if (!this.inBounds(ix, iz)) return false;
        const px = (ix + 0.5) * this.cell - this.half - cx, pz = (iz + 0.5) * this.cell - this.half - cz;
        const lx = px * ca - pz * sa, lz = px * sa + pz * ca;
        if (Math.abs(lx) <= hw && Math.abs(lz) <= hd && this.data[iz * this.n + ix]) return false;
      }
    }
    return true;
  }
}

/** Coarse grid of "how tall is whatever stands here" (houses, trees...) used for camera occlusion. */
export class HeightGrid {
  constructor(size = WORLD_R * 2, cell = 2) {
    this.cell = cell;
    this.half = size / 2;
    this.n = Math.ceil(size / cell);
    this.data = new Float32Array(this.n * this.n);
  }
  _ix(x) { return Math.floor((x + this.half) / this.cell); }
  add(x, z, r, h) {
    const x0 = this._ix(x - r), x1 = this._ix(x + r), z0 = this._ix(z - r), z1 = this._ix(z + r);
    for (let iz = z0; iz <= z1; iz++) {
      for (let ix = x0; ix <= x1; ix++) {
        if (ix < 0 || iz < 0 || ix >= this.n || iz >= this.n) continue;
        const cx = (ix + 0.5) * this.cell - this.half, cz = (iz + 0.5) * this.cell - this.half;
        if ((cx - x) ** 2 + (cz - z) ** 2 <= r * r + this.cell * this.cell * 0.5) {
          const k = iz * this.n + ix;
          if (h > this.data[k]) this.data[k] = h;
        }
      }
    }
  }
  at(x, z) {
    const ix = this._ix(x), iz = this._ix(z);
    if (ix < 0 || iz < 0 || ix >= this.n || iz >= this.n) return 0;
    return this.data[iz * this.n + ix];
  }
}
